<template>
  <Teleport to="body">
    <Transition name="toast-fade" appear>
      <div
        v-if="isVisible"
        class="toast-wrapper"
        :class="[`toast--${type}`, { 'toast--rtl': isRTL }]"
        role="alert"
        :aria-live="type === 'error' ? 'assertive' : 'polite'"
        :aria-label="`${type} إشعار: ${message}`"
      >
        <div class="toast-container">
          <!-- Progress bar -->
          <div class="toast-progress" :style="{ animationDuration: `${duration}ms` }"></div>

          <!-- Main card content -->
          <div class="toast-card">
            <!-- Icon section -->
            <div class="toast-icon-wrapper">
              <div class="toast-icon" :class="`icon-${type}`">
                <component :is="iconComponent" class="icon-svg" />
              </div>
            </div>

            <!-- Text content -->
            <div class="toast-text-content">
              <div v-if="title" class="toast-title">{{ title }}</div>
              <p class="toast-message">{{ message }}</p>
            </div>

            <!-- Close button -->
            <button
              class="toast-close-btn"
              @click="hideToast"
              aria-label="إغلاق الإشعار"
              type="button"
            >
              <svg
                class="close-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, h, defineProps, defineEmits } from 'vue'

// Props
const props = defineProps({
  message: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'success',
    validator: (value) => ['success', 'error', 'warning', 'info'].includes(value),
  },
  duration: {
    type: Number,
    default: 4000,
  },
  position: {
    type: String,
    default: 'top-right',
    validator: (value) => [
      'top-left', 'top-center', 'top-right',
      'bottom-left', 'bottom-center', 'bottom-right'
    ].includes(value),
  },
  persistent: {
    type: Boolean,
    default: false,
  },
})

// Emits
const emit = defineEmits(['close'])

// State
const isVisible = ref(true)
const isRTL = ref(true)

// Computed properties
const iconComponent = computed(() => {
  const icons = {
    success: () => h('svg', {
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '3',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    }, [
      h('polyline', { points: '20 6 9 17 4 12' })
    ]),
    
    error: () => h('svg', {
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '2.5',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    }, [
      h('circle', { cx: '12', cy: '12', r: '10' }),
      h('line', { x1: '15', y1: '9', x2: '9', y2: '15' }),
      h('line', { x1: '9', y1: '9', x2: '15', y2: '15' })
    ]),
    
    warning: () => h('svg', {
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '2.5',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    }, [
      h('polygon', { points: '12 2 15.09 10.26 24 12.75 17.18 19.58 18.34 28.88 12 24.77 5.66 28.88 6.82 19.58 0 12.75 8.91 10.26' })
    ]),
    
    info: () => h('svg', {
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '2.5',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    }, [
      h('circle', { cx: '12', cy: '12', r: '10' }),
      h('line', { x1: '12', y1: '16', x2: '12', y2: '12' }),
      h('line', { x1: '12', y1: '8', x2: '12.01', y2: '8' })
    ])
  }
  
  return icons[props.type] || icons.info
})

// Methods
const hideToast = () => {
  isVisible.value = false
  emit('close')
}

// Auto-hide timer
let timeoutId = null

const startAutoHide = () => {
  if (!props.persistent && props.duration > 0) {
    timeoutId = setTimeout(hideToast, props.duration)
  }
}

const clearAutoHide = () => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
}

// Lifecycle
onMounted(() => {
  startAutoHide()
})

onUnmounted(() => {
  clearAutoHide()
})
</script>

<style scoped>
/* Toast wrapper - positioning */
.toast-wrapper {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 50;
  pointer-events: none;
  direction: rtl;
}

.toast-wrapper.toast--rtl {
  right: 20px;
  left: auto;
}

/* Container with pointer events */
.toast-container {
  pointer-events: auto;
  position: relative;
}

/* Progress bar */
.toast-progress {
  position: absolute;
  top: 0;
  left: 0;
  height: 3px;
  width: 100%;
  border-radius: 99px;
  transform-origin: left;
  animation: slideProgress linear forwards;
  z-index: 0;
}

@keyframes slideProgress {
  0% {
    width: 100%;
  }
  100% {
    width: 0%;
  }
}

/* Main toast card */
.toast-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow:
    0 10px 38px 0 rgba(0, 0, 0, 0.15),
    0 4px 6px 0 rgba(0, 0, 0, 0.08);
  min-width: 320px;
  max-width: 520px;
  font-family: 'MTNBrighterSans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* Icon wrapper */
.toast-icon-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  animation: iconScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes iconScale {
  0% {
    transform: scale(0.6);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.icon-svg {
  width: 24px;
  height: 24px;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* Text content */
.toast-text-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toast-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: inherit;
}

.toast-message {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: inherit;
  margin: 0;
  word-wrap: break-word;
  opacity: 0.95;
}

/* Close button */
.toast-close-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  color: inherit;
  padding: 0;
  font-size: 0;
  line-height: 0;
}

.close-icon {
  width: 16px;
  height: 16px;
}

.toast-close-btn:hover {
  background: rgba(0, 0, 0, 0.16);
  transform: scale(1.08);
}

.toast-close-btn:active {
  transform: scale(0.96);
}

/* Toast type variants */
/* Success */
.toast--success .toast-card {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.toast--success .toast-progress {
  background: rgba(255, 255, 255, 0.4);
}

.toast--success .icon-success {
  background: rgba(255, 255, 255, 0.2);
  color: #ecfdf5;
}

.toast--success .toast-close-btn {
  background: rgba(255, 255, 255, 0.15);
}

.toast--success .toast-close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

/* Error */
.toast--error .toast-card {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.toast--error .toast-progress {
  background: rgba(255, 255, 255, 0.4);
}

.toast--error .icon-error {
  background: rgba(255, 255, 255, 0.2);
  color: #fef2f2;
}

.toast--error .toast-close-btn {
  background: rgba(255, 255, 255, 0.15);
}

.toast--error .toast-close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

/* Warning */
.toast--warning .toast-card {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.toast--warning .toast-progress {
  background: rgba(255, 255, 255, 0.4);
}

.toast--warning .icon-warning {
  background: rgba(255, 255, 255, 0.2);
  color: #fffbeb;
}

.toast--warning .toast-close-btn {
  background: rgba(255, 255, 255, 0.15);
}

.toast--warning .toast-close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

/* Info */
.toast--info .toast-card {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.toast--info .toast-progress {
  background: rgba(255, 255, 255, 0.4);
}

.toast--info .icon-info {
  background: rgba(255, 255, 255, 0.2);
  color: #eff6ff;
}

.toast--info .toast-close-btn {
  background: rgba(255, 255, 255, 0.15);
}

.toast--info .toast-close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

/* Animations */
.toast-fade-enter-active {
  animation: toastEnter 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toast-fade-leave-active {
  animation: toastExit 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes toastEnter {
  from {
    opacity: 0;
    transform: translateX(120%) rotateZ(-5deg);
  }
  to {
    opacity: 1;
    transform: translateX(0) rotateZ(0deg);
  }
}

@keyframes toastExit {
  from {
    opacity: 1;
    transform: translateX(0) rotateZ(0deg);
  }
  to {
    opacity: 0;
    transform: translateX(120%) rotateZ(5deg);
  }
}

/* Responsive design */
@media (max-width: 640px) {
  .toast-wrapper {
    top: 16px;
    right: 16px;
    left: 16px;
  }

  .toast-card {
    min-width: unset;
    max-width: 100%;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 10px;
  }

  .toast-title {
    font-size: 15px;
  }

  .toast-message {
    font-size: 13px;
  }

  .toast-icon {
    width: 40px;
    height: 40px;
  }

  .icon-svg {
    width: 22px;
    height: 22px;
  }

  .toast-close-btn {
    width: 28px;
    height: 28px;
  }

  .close-icon {
    width: 14px;
    height: 14px;
  }
}

@media (max-width: 480px) {
  .toast-wrapper {
    top: 12px;
    right: 12px;
    left: 12px;
  }

  .toast-card {
    gap: 10px;
    padding: 12px 14px;
    border-radius: 8px;
  }

  .toast-title {
    font-size: 14px;
  }

  .toast-message {
    font-size: 12px;
    line-height: 1.4;
  }

  .toast-icon {
    width: 36px;
    height: 36px;
    min-width: 36px;
  }

  .icon-svg {
    width: 20px;
    height: 20px;
  }

  .toast-close-btn {
    width: 26px;
    height: 26px;
  }
}

/* Accessibility - reduced motion */
@media (prefers-reduced-motion: reduce) {
  .toast-fade-enter-active,
  .toast-fade-leave-active {
    animation: none;
    transition: opacity 0.2s ease;
  }

  .toast-icon {
    animation: none;
  }

  .toast-progress {
    animation: none;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .toast-card {
    box-shadow:
      0 10px 38px 0 rgba(0, 0, 0, 0.4),
      0 4px 6px 0 rgba(0, 0, 0, 0.2);
  }
}

/* Progress bar */
.toast-progress {
  position: absolute;
  top: 0;
  left: 0;
  height: 3px;
  width: 100%;
  transform-origin: left;
  animation: progress linear forwards;
}

@keyframes progress {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}

/* Icon styling */
.toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.icon-svg {
  width: 20px;
  height: 20px;
  z-index: 1;
  position: relative;
}

.icon-circle::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  opacity: 0.1;
  animation: iconPulse 2s infinite;
}

@keyframes iconPulse {
  0%, 100% { transform: scale(1); opacity: 0.1; }
  50% { transform: scale(1.2); opacity: 0.2; }
}

/* Content */
.toast-content {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.toast-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  margin-bottom: 4px;
  color: inherit;
}

.toast-message {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: inherit;
  word-wrap: break-word;
  opacity: 0.9;
}

/* Close button */
.toast-close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  color: inherit;
  opacity: 0.7;
  margin-top: 2px;
}

.toast-close:hover {
  background: rgba(0, 0, 0, 0.1);
  opacity: 1;
  transform: scale(1.05);
}

.toast-close:active {
  transform: scale(0.95);
}

.toast-close svg {
  width: 16px;
  height: 16px;
}

/* Toast variants */
.toast--success .toast-card {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.95) 0%, rgba(21, 128, 61, 0.95) 100%);
  border-color: rgba(34, 197, 94, 0.3);
  color: white;
}

.toast--success .toast-progress {
  background: rgba(255, 255, 255, 0.3);
}

.toast--success .icon-circle {
  color: rgba(255, 255, 255, 0.9);
}

.toast--success .icon-circle::before {
  background: white;
}

.toast--error .toast-card {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%);
  border-color: rgba(239, 68, 68, 0.3);
  color: white;
}

.toast--error .toast-progress {
  background: rgba(255, 255, 255, 0.3);
}

.toast--error .icon-circle {
  color: rgba(255, 255, 255, 0.9);
}

.toast--error .icon-circle::before {
  background: white;
}

.toast--warning .toast-card {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(180, 83, 9, 0.95) 100%);
  border-color: rgba(245, 158, 11, 0.3);
  color: white;
}

.toast--warning .toast-progress {
  background: rgba(255, 255, 255, 0.3);
}

.toast--warning .icon-circle {
  color: rgba(255, 255, 255, 0.9);
}

.toast--warning .icon-circle::before {
  background: white;
}

.toast--info .toast-card {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.95) 0%, rgba(29, 78, 216, 0.95) 100%);
  border-color: rgba(59, 130, 246, 0.3);
  color: white;
}

.toast--info .toast-progress {
  background: rgba(255, 255, 255, 0.3);
}

.toast--info .icon-circle {
  color: rgba(255, 255, 255, 0.9);
}

.toast--info .icon-circle::before {
  background: white;
}

/* Animations */
.toast-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.8);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.9);
}

/* Responsive design */
@media (max-width: 640px) {
  .toast-wrapper {
    top: 16px;
    right: 16px;
    left: 16px;
  }
  
  .toast-wrapper.toast--rtl {
    right: 16px;
    left: 16px;
  }

  .toast-card {
    min-width: unset;
    max-width: unset;
    width: 100%;
    padding: 16px;
    border-radius: 12px;
  }

  .toast-title {
    font-size: 15px;
  }

  .toast-message {
    font-size: 13px;
  }

  .icon-circle {
    width: 36px;
    height: 36px;
  }

  .icon-svg {
    width: 18px;
    height: 18px;
  }

  .toast-close {
    width: 28px;
    height: 28px;
  }

  .toast-close svg {
    width: 14px;
    height: 14px;
  }
}

@media (max-width: 480px) {
  .toast-wrapper {
    top: 12px;
    right: 12px;
    left: 12px;
  }
  
  .toast-wrapper.toast--rtl {
    right: 12px;
    left: 12px;
  }

  .toast-card {
    padding: 14px;
    gap: 12px;
    border-radius: 10px;
  }

  .toast-title {
    font-size: 14px;
  }

  .toast-message {
    font-size: 12px;
  }

  .icon-circle {
    width: 32px;
    height: 32px;
  }

  .icon-svg {
    width: 16px;
    height: 16px;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .toast-card {
    background: rgba(17, 24, 39, 0.95);
    border-color: rgba(75, 85, 99, 0.3);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.3),
      0 8px 16px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  .toast-close {
    background: rgba(255, 255, 255, 0.1);
  }

  .toast-close:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active {
    transition: opacity 0.2s ease;
  }

  .toast-enter-from {
    transform: none;
  }

  .toast-leave-to {
    transform: none;
  }

  .toast-progress {
    animation: none;
  }

  @keyframes iconPulse {
    0%, 100% { transform: none; opacity: 0.1; }
  }
}
</style>